<template>
  <div class="d-flex justify-content-between align-items-center pt-4 px-3">
    <div>
      <h6 class="title">{{ title }}</h6>
    </div>
    <div class="d-flex gap-4 align-items-center">
      <div class="d-flex align-items-center gap-2">
        <custom-date
          v-model="selectedDate"
          :border="false"
          :newDays="days"
          @date-changed="onDateTypeChange"
          @show-custom-range="onCustomRangeChange"
        />
        <div v-if="showCustomRange">
          <date-range-picker :start="startDate" :end="endDate" :locale="$i18n.locale" @selected="changeCustomDate" />
        </div>
      </div>

      <!-- Group By Toggle -->
      <div v-if="shouldShowGroupByToggle" class="d-flex align-items-center">
        <toggle-button-group :options="groupByOptions" @change="onGroupByChange" />
      </div>

      <div class="d-flex align-items-center gap-2">
        <label class="time-label">{{ $t('flexigo.common.from') }}</label>
        <input v-model="fromHour" type="time" class="form-control time-input" :max="toHour" @change="onTimeChange" />

        <label class="time-label">{{ $t('flexigo.common.to') }}</label>
        <input v-model="toHour" type="time" class="form-control time-input" :min="fromHour" @change="onTimeChange" />
      </div>
      <div class="export-menu-container" :class="{ disabled: !hasData }">
        <img
          :src="`${baseUrl}icons/download.svg`"
          :class="['ms-2', hasData ? 'has-cursor-pointer' : 'disabled-icon']"
          width="16px"
          height="16px"
          alt="Export"
          @click.stop="hasData && toggleExportMenu()"
        />
        <div v-if="showExportMenu" ref="exportDropdown" class="export-dropdown">
          <div class="export-option" @click="onDownloadImage">
            <i class="bi bi-image"></i>
            <span>{{ $t('flexigo.dashboard.downloadAsImage') }}</span>
          </div>
          <div class="export-option" @click="onDownloadExcel">
            <i class="bi bi-file-earmark-spreadsheet"></i>
            <span>{{ $t('flexigo.dashboard.downloadAsExcel') }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { startOfWeek, endOfWeek } from 'date-fns';
import DateRangePicker from '@vektor/date-picker';
import { intToTimeStr } from '@/helpers/dateTime';
import customDate from '../../../components/date.vue';
import toggleButtonGroup from '../../../components/toggleButton/toggleButtonGroup.vue';

export default {
  name: 'ChartHeader',
  components: {
    customDate,
    DateRangePicker,
    toggleButtonGroup,
  },
  props: {
    title: {
      type: String,
      required: true,
    },
    hasData: {
      type: Boolean,
      default: true,
    },
    showGroupByToggle: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const today = new Date();

    return {
      selectedDate: 'thisWeek',
      showCustomRange: false,
      showExportMenu: false,
      startDate: startOfWeek(today, { weekStartsOn: 1 }),
      endDate: endOfWeek(today, { weekStartsOn: 1 }),
      fromHour: null,
      toHour: null,
      days: ['today', 'thisWeek', 'lastSevenDays', 'thisMonth', 'lastThirtyDays', 'lastMonth', 'customDate'],
      groupBy: 'date',
      baseUrl: process.env.BASE_URL,
    };
  },
  computed: {
    operationalHours() {
      return this.$store.getters['onDemandDashboard/operationalHoursList'] || {};
    },
    isSingleDay() {
      if (!this.startDate || !this.endDate) return false;
      const start = new Date(this.startDate);
      const end = new Date(this.endDate);
      start.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);
      return start.getTime() === end.getTime();
    },
    shouldShowGroupByToggle() {
      return this.showGroupByToggle && this.selectedDate !== 'today' && !this.isSingleDay;
    },
    groupByOptions() {
      let options = [
        { value: 'date', label: this.$t('flexigo.dashboard.byDate') },
        { value: 'timeOfDay', label: this.$t('flexigo.dashboard.byTimeOfDay') },
        { value: 'weekday', label: this.$t('flexigo.dashboard.byWeekday') },
      ];

      // For 'today', remove 'by Date' option (can't show multiple hours as dates)
      if (this.selectedDate === 'today') {
        options = options.filter((opt) => opt.value !== 'date');
      }

      return options.map((option) => ({
        label: option.label,
        selected: option.value === this.groupBy,
      }));
    },
  },
  watch: {
    operationalHours: {
      handler() {
        const { scheduleType, periodList } = this.operationalHours;
        if (scheduleType !== 'ALL_DAY') {
          const { start, end } = periodList?.[0] || {};

          this.fromHour = intToTimeStr(start);
          this.toHour = intToTimeStr(end);

          // Emit change to send initial operational hours to service
          this.$nextTick(() => {
            this.emitChange();
          });
        }
      },
    },
    selectedDate(newVal) {
      // When 'today' is selected, always set groupBy to 'date' (shows hourly by default)
      if (newVal === 'today' && this.groupBy !== 'date') {
        this.groupBy = 'date';
        this.$emit('group-by-change', this.groupBy);
      }
    },
    isSingleDay(newVal) {
      // When single day is selected (including custom date), reset groupBy to 'date'
      if (newVal && this.groupBy !== 'date') {
        this.groupBy = 'date';
        this.$emit('group-by-change', this.groupBy);
      }
    },
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    handleClickOutside(event) {
      const exportContainer = this.$el.querySelector('.export-menu-container');
      if (this.showExportMenu && exportContainer && !exportContainer.contains(event.target)) {
        this.showExportMenu = false;
      }
    },
    onDateTypeChange(e) {
      // Convert string dates to Date objects for DateRangePicker
      this.startDate = e.fromDate ? new Date(e.fromDate) : e.fromDate;
      this.endDate = e.toDate ? new Date(e.toDate) : e.toDate;
      this.selectedDate = e.type;
      this.emitChange();
    },
    onCustomRangeChange(data) {
      this.showCustomRange = data;
    },
    changeCustomDate(date) {
      this.startDate = date.raw?.startDate;
      this.endDate = date.raw?.endDate;
      this.selectedDate = 'customDate';
      this.emitChange();
    },
    onTimeChange() {
      this.emitChange();
    },
    emitChange() {
      this.$emit('filter-change', {
        startDate: this.startDate,
        endDate: this.endDate,
        fromHour: this.fromHour,
        toHour: this.toHour,
        selectedDate: this.selectedDate,
      });
    },
    toggleExportMenu() {
      this.showExportMenu = !this.showExportMenu;
    },
    closeExportMenu() {
      this.showExportMenu = false;
    },
    onDownloadImage() {
      this.$emit('download', 'image');
      this.closeExportMenu();
    },
    onDownloadExcel() {
      this.$emit('download', 'excel');
      this.closeExportMenu();
    },
    onGroupByChange({ index }) {
      const groupByValues = ['date', 'timeOfDay', 'weekday'];
      this.groupBy = groupByValues[index];
      this.$emit('group-by-change', this.groupBy);
    },
  },
};
</script>

<style scoped>
.title {
  color: #363647;
  font-weight: bold;
  font-size: 16px;
  margin-bottom: 0;
}

.time-label {
  color: #363647;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 0;
}

.time-input {
  height: 38px;
  border: 1px solid #e6eaee;
  border-radius: 4px;
  padding: 4px;
  font-size: 14px;
}

.time-input:focus {
  border-color: #4a90e2;
  outline: none;
  box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.1);
}
input[type='time']::-webkit-calendar-picker-indicator {
  opacity: 0;
  width: 100%;
  height: 100%;
  position: absolute;
  left: 0;
  top: 0;
  cursor: pointer;
}

input[type='time'] {
  position: relative;
}

.download-btn {
  background: transparent;
  border: 1px solid #e6eaee;
  border-radius: 4px;
  padding: 8px 12px;
  cursor: pointer;
  color: #363647;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 38px;
  width: 38px;
}

.download-btn:hover {
  background: #f5f7fa;
  border-color: #4a90e2;
  color: #4a90e2;
}

.download-btn i {
  font-size: 16px;
}

.disabled-icon {
  opacity: 0.3;
  cursor: not-allowed !important;
}

.export-menu-container {
  position: relative;
}

.export-menu-container.disabled {
  pointer-events: none;
}

.export-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: white;
  border: 1px solid #e6eaee;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  min-width: 200px;
  z-index: 1000;
  overflow: hidden;
}

.export-option {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  color: #363647;
  font-size: 14px;
}

.export-option:hover {
  background-color: #f5f7fa;
}

.export-option i {
  font-size: 16px;
  color: #4a90e2;
}

.export-option span {
  font-weight: 500;
}
</style>
